// File: src/pages/PageMenu/AccountsMenu.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import {
  Container,
  Typography,
  ButtonBase,
  Paper,
  useTheme,
  useMediaQuery,
  Box,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from '@mui/icons-material/Today';
import DescriptionIcon from "@mui/icons-material/Description";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import PostAddIcon from '@mui/icons-material/PostAdd';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalculateIcon from '@mui/icons-material/Calculate';

export default function AccountsMenu() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';

  // 1. Definitions (التعاريف) مع الألوان المودرن المخصصة
  const definitionsItems = [
    hasPermission(PERMISSIONS.CHART_OF_ACCOUNTS_VIEW) && {
      name: "دليل الحسابات", 
      icon: <AccountTreeIcon />,
      color: "#6366F1",
      bgColor: "rgba(99, 102, 241, 0.06)",
      link: "/chart-of-accounts"
    },
    hasPermission(PERMISSIONS.GL_SETTINGS_VIEW) && {
      name: "توجيه الحسابات",
      icon: <SettingsIcon />,
      color: "#475569",
      bgColor: "rgba(71, 85, 105, 0.06)",
      link: "/gl-settings"
    },
    hasPermission(PERMISSIONS.SAFE_VIEW) && {
      name: "الصناديق",
      icon: <AccountBalanceWalletIcon />,
      color: "#78350F",
      bgColor: "rgba(120, 53, 15, 0.06)",
      link: "/safes"
    },
    hasPermission(PERMISSIONS.BANK_VIEW) && {
      name: "البنوك",
      icon: <AccountBalanceIcon />,
      color: "#2563EB",
      bgColor: "rgba(37, 99, 235, 0.06)",
      link: "/banks"
    },
    hasPermission(PERMISSIONS.PAYMENT_METHOD_VIEW) && {
      name: "طرق الدفع",
      icon: <PaymentIcon />,
      color: "#EC4899",
      bgColor: "rgba(236, 72, 153, 0.06)",
      link: "/payment-methods"
    },
  ].filter(Boolean);

  // 2. Operations (العمليات) مع الألوان المودرن المخصصة
  const operationsItems = [
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "قيد افتتاحي",
      icon: <PostAddIcon />,
      color: "#64748B",
      bgColor: "rgba(100, 116, 139, 0.06)",
      link: "/journal-entries/new?type=2"
    },
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "سند قيد",
      icon: <DescriptionIcon />,
      color: "#F59E0B",
      bgColor: "rgba(245, 158, 11, 0.06)",
      link: "/journal-entries/new?type=1"
    },
    hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && {
      name: "سند قبض",
      icon: <ReceiptIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/vouchers/general-receipt"
    },
    hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && {
      name: "سند صرف",
      icon: <PaymentIcon />,
      color: "#EF4444",
      bgColor: "rgba(239, 68, 68, 0.06)",
      link: "/vouchers/general-payment"
    },
    hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && {
      name: "سند قبض عميل",
      icon: <RequestQuoteIcon />,
      color: "#0D9488",
      bgColor: "rgba(13, 148, 136, 0.06)",
      link: "/vouchers/customer-receipt"
    },
    hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && {
      name: "سند صرف مورد",
      icon: <ReceiptLongIcon />,
      color: "#E11D48",
      bgColor: "rgba(225, 29, 72, 0.06)",
      link: "/vouchers/supplier-payment"
    },
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_CREATE) && {
      name: "سند ضريبي",
      icon: <CalculateIcon />,
      color: "#8B5CF6",
      bgColor: "rgba(139, 92, 246, 0.06)",
      link: "/vouchers/tax-payment"
    }
  ].filter(Boolean);

  // 3. Reports (التقارير) مع الألوان المودرن المخصصة
  const reportsItems = [
    hasPermission(PERMISSIONS.VOUCHER_VIEW) && {
      name: "قائمة السندات",
      icon: <ListAltIcon />,
      color: "#3B82F6",
      bgColor: "rgba(59, 130, 246, 0.06)",
      link: "/vouchers-list"
    },
    hasPermission(PERMISSIONS.JOURNAL_ENTRY_VIEW) && {
      name: "القيود اليومية",
      icon: <DescriptionIcon />,
      color: "#D97706",
      bgColor: "rgba(217, 119, 6, 0.06)",
      link: "/journal-entries"
    },
    hasPermission(PERMISSIONS.ACCOUNT_STATEMENT_VIEW) && {
      name: "كشف حساب",
      icon: <AssessmentIcon />,
      color: "#EC4899",
      bgColor: "rgba(236, 72, 153, 0.06)",
      link: "/reports/account-statement"
    },
    hasPermission(PERMISSIONS.TRIAL_BALANCE_VIEW) && {
      name: "ميزان المراجعة",
      icon: <AccountBalanceIcon />,
      color: "#7C3AED",
      bgColor: "rgba(124, 58, 237, 0.06)",
      link: "/reports/trial-balance"
    },
    hasPermission(PERMISSIONS.BALANCE_SHEET_VIEW) && {
      name: "الميزانية العمومية",
      icon: <AccountBalanceWalletIcon />,
      color: "#06B6D4",
      bgColor: "rgba(6, 182, 212, 0.06)",
      link: "/reports/balance-sheet"
    },
    hasPermission(PERMISSIONS.INCOME_STATEMENT_VIEW) && {
      name: "قائمة الدخل",
      icon: <TrendingUpIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/reports/income-statement"
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: "تقرير الحركة اليومية",
      icon: <TodayIcon />,
      color: "#F97316",
      bgColor: "rgba(249, 115, 22, 0.06)",
      link: "/reports/daily-movement"
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: "الاقرار الضريبي",
      icon: <DescriptionIcon />,
      color: "#0F766E",
      bgColor: "rgba(15, 118, 110, 0.06)",
      link: "/VatReportPage"
    }
  ].filter(Boolean);

  // دالة عرض الأيقونات المطورة والمثبتة بحد أقصى 4 كروت في الصف الواحد لتلائم باقي النظام
  const renderGridItems = (items) => (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',          // جوال: كرتين بالصف
          sm: 'repeat(3, 1fr)',          // تابلت: 3 كروت بالصف
          md: 'repeat(4, 1fr)',          // لابتوب: 4 كروت كحد أقصى
          lg: 'repeat(4, 1fr)',          // شاشات مكتبية: تثبيت 4 كروت بالصف
        },
        width: '100%'
      }}
    >
      {items.map((item, index) => (
        <ButtonBase
          key={index}
          onClick={() => navigate(item.link)}
          component={Paper}
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 3, sm: 4 },
            borderRadius: '24px', 
            backgroundColor: "#ffffff",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "pointer",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`,
              borderColor: item.color,
              "& .icon-container": {
                transform: 'scale(1.08) rotate(3deg)',
                backgroundColor: item.color,
                color: '#ffffff'
              }
            },
            "&:active": { transform: "translateY(-1px)" }
          }}
        >
          {/* حاوية الأيقونة الدائرية التفاعلية */}
          <Box
            className="icon-container"
            sx={{
              width: { xs: 55, sm: 65 },
              height: { xs: 55, sm: 65 },
              borderRadius: '18px',
              backgroundColor: item.bgColor,
              color: item.color,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
              transition: 'all 0.3s ease',
              '& svg': { fontSize: { xs: 26, sm: 30 } }
            }}
          >
            {item.icon}
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              color: '#1E293B',
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              letterSpacing: '-0.3px',
              lineHeight: 1.4,
              px: 0.5
            }}
          >
            {item.name}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 5, 
        mb: 5, 
        px: { xs: 2, md: 4 },
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* هيدر تدرج النصوص الفاخر العصري الموحد */}
      <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'start' } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            mb: 1
          }}
        >
          {t('accountsMenu.title') || "المالية والحسابات"}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
         
        </Typography>
      </Box>

      {/* 1. Definitions Section */}
      {definitionsItems.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h6" 
            sx={{
              fontWeight: 800, 
              mb: 3, 
              color: '#1E293B',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '1.2rem',
              '&::after': {
                content: '""',
                flexGrow: 1,
                height: '1px',
                backgroundColor: 'rgba(226, 232, 240, 0.8)',
                marginLeft: isRTL ? 0 : 2,
                marginRight: isRTL ? 2 : 0
              }
            }}
          >
            🗂️ التعاريف
          </Typography>
          {renderGridItems(definitionsItems)}
        </Box>
      )}

      {/* 2. Operations Section */}
      {operationsItems.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h6" 
            sx={{
              fontWeight: 800, 
              mb: 3, 
              color: '#1E293B',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '1.2rem',
              '&::after': {
                content: '""',
                flexGrow: 1,
                height: '1px',
                backgroundColor: 'rgba(226, 232, 240, 0.8)',
                marginLeft: isRTL ? 0 : 2,
                marginRight: isRTL ? 2 : 0
              }
            }}
          >
            💰 العمليات
          </Typography>
          {renderGridItems(operationsItems)}
        </Box>
      )}

      {/* 3. Reports Section */}
      {reportsItems.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            sx={{
              fontWeight: 800, 
              mb: 3, 
              color: '#1E293B',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '1.2rem',
              '&::after': {
                content: '""',
                flexGrow: 1,
                height: '1px',
                backgroundColor: 'rgba(226, 232, 240, 0.8)',
                marginLeft: isRTL ? 0 : 2,
                marginRight: isRTL ? 2 : 0
              }
            }}
          >
            📊 التقارير
          </Typography>
          {renderGridItems(reportsItems)}
        </Box>
      )}
    </Container>
  );
}